import logger from '@/utils/logger';
import { getCachedUIPreferences, cacheUIPreferences } from '@/utils/uiPreferences.storage';

class PageWindowNavigationService {
  constructor() {
    this.DEFAULT_MODE = 'multi';
    this.AVAILABLE_MODES = ['single', 'multi'];
    this.EVENT_NAME = 'pageWindowModeChanged';
    /** Paths of currently open page windows — used to detect tab switches vs new navigation */
    this.openWindowPaths = new Set();
  }

  setOpenWindowPaths(paths) {
    this.openWindowPaths = new Set(Array.isArray(paths) ? paths : []);
  }

  clearOpenWindowPaths() {
    this.openWindowPaths = new Set();
  }

  isOpenWindowPath(fullPath) {
    return this.openWindowPaths.has(fullPath);
  }

  /**
   * Switching between already-open windows — skip global loader and sidebar refresh.
   */
  isWindowTabSwitch(from, to) {
    if (!this.isMultiWindowEnabled()) return false;
    if (!from?.fullPath || !to?.fullPath || from.fullPath === to.fullPath) return false;
    if (String(from.params?.id ?? '') !== String(to.params?.id ?? '')) return false;
    if (!from.path.includes('/dashboard') || !to.path.includes('/dashboard')) return false;
    return this.isOpenWindowPath(from.fullPath) && this.isOpenWindowPath(to.fullPath);
  }

  getCurrentMode() {
    try {
      return getCachedUIPreferences().pageWindowMode;
    } catch (error) {
      logger.error('Error getting page window mode:', error);
      return this.DEFAULT_MODE;
    }
  }

  isMultiWindowEnabled() {
    return this.getCurrentMode() === 'multi';
  }

  /**
   * Apply page window mode without persisting.
   * @param {string} mode
   */
  applyMode(mode) {
    try {
      if (!this.AVAILABLE_MODES.includes(mode)) {
        logger.warn(`Invalid page window mode: ${mode}. Using default.`);
        mode = this.DEFAULT_MODE;
      }

      if (mode === 'single') {
        this.clearOpenWindowPaths();
      }

      window.dispatchEvent(new CustomEvent(this.EVENT_NAME, {
        detail: { mode },
      }));

      return true;
    } catch (error) {
      logger.error('Error applying page window mode:', error);
      return false;
    }
  }

  setMode(mode, saveToStorage = true) {
    try {
      if (!this.AVAILABLE_MODES.includes(mode)) {
        logger.warn(`Invalid page window mode: ${mode}. Using default.`);
        mode = this.DEFAULT_MODE;
      }

      if (saveToStorage) {
        const preferences = getCachedUIPreferences();
        cacheUIPreferences({ ...preferences, pageWindowMode: mode });
      }

      return this.applyMode(mode);
    } catch (error) {
      logger.error('Error setting page window mode:', error);
      return false;
    }
  }

  getAvailableModes() {
    return [
      {
        value: 'single',
        label: 'Single page',
        description: 'Classic navigation — one page at a time. Previous page is replaced when you navigate.',
        icon: 'bi bi-file-earmark',
      },
      {
        value: 'multi',
        label: 'Multi-window',
        description: 'Open pages in separate windows. Switch between Customers, Vendors, and more without losing your place.',
        icon: 'bi bi-window-stack',
      },
    ];
  }

  getModeLabel(mode) {
    const config = this.getAvailableModes().find((m) => m.value === mode);
    return config ? config.label : mode;
  }

  isValidMode(mode) {
    return this.AVAILABLE_MODES.includes(mode);
  }
}

export default new PageWindowNavigationService();
